import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, InlineAlert } from '../components/BaseComponents'
import { FormEvent, useContext, useState } from 'react';
import usePublicApi from '../hooks/usePublicApi';
import { FORM_INPUT_TEXT_CLASS, FORM_LABEL_CLASS } from '../lib/formStyles';
import { FOCUS_VISIBLE_OUTLINE_INSET, INPUT_FOCUS_RING, TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus';
import { getCSRFToken, consumeLogoutNotConfirmed, hasApiErrorCode } from '../lib/utils';
import routes from '../components/Router/routes';
import { userContext } from '../context/UserContext';
import AccountOnHold from './AccountOnHold';

function Login() {
    const publicApi = usePublicApi();
    const navigate = useNavigate();
    const { fetchUserData } = useContext(userContext);

    const {state: navState } = useLocation();

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(navState ? navState.error : '');
    const message = navState?.message;
    const accountOnHold = Boolean(navState?.accountOnHold);
    const [notVerified, setNotVerified] = useState(false);
    const [onHold, setOnHold] = useState(false);
    const [resent, setResent] = useState(false);
    // Issue #759 (security review correction): read (and consume) once on mount - a one-time
    // warning for whichever sign-out attempt landed the user here, not a persistent banner that
    // would keep reappearing on every later visit to this page.
    const [logoutNotConfirmed] = useState(() => consumeLogoutNotConfirmed());

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const onFormSubmit = (e: FormEvent) => {
       e.preventDefault();
       onSubmit();
       return false; 
    }

    const onSubmit = async () => {
        setSubmitting(true)
        setError('');

        try {
            await publicApi.post('/user/login', {
                email, password,
                _csrf: await getCSRFToken(),
            });
            // Refresh the shared UserContext before navigating - it otherwise only fetches once on
            // mount, so a custom redirectTo target (e.g. AcceptInvite.tsx) would see stale,
            // logged-out userData and loop back to "please log in" even though login succeeded.
            await fetchUserData();
            navigate(navState?.redirectTo || routes.landingPage.path)
        } catch(error: unknown) {
            console.warn('Login failed.')
			setSubmitting(false);
			const loginError = typeof error === 'object' && error !== null && 'body' in error &&
                typeof error.body === 'object' && error.body !== null && 'loginError' in error.body &&
                typeof error.body.loginError === 'string' ? error.body.loginError : undefined;
            setNotVerified(hasApiErrorCode(error, 'EMAIL_NOT_VERIFIED'))
            setOnHold(hasApiErrorCode(error, 'ACCOUNT_LOCKED'))
            setResent(false)
            setError(loginError || 'Could not log in. Please try again later.')
        }
    }

    // Public, keyed by email: an unverified account cannot log in, so there is no session to use.
    const onResendVerification = async () => {
        setSubmitting(true)
        try {
            await publicApi.post('/resendVerification', { email, _csrf: await getCSRFToken() });
            setResent(true)
        } catch {
            console.warn('Verification email could not be sent.')
            setError('Could not send the verification email. Please try again later.')
        } finally {
            setSubmitting(false)
        }
    }
    if (onHold) {
        return <AccountOnHold email={email} password={password} onBack={() => { setOnHold(false); setPassword(''); setError('') }} />
    }
    return (
        <div className="content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16 mb-8">
            <div className="text-charcoal-100 text-2xl text-center">Welcome</div>
            <div className="flex text-center mt-8 text-sm">
                <Link className={`w-1/2 text-charcoal-100 p-2 hover:text-white ${FOCUS_VISIBLE_OUTLINE_INSET}`} to={routes.publicSignUp.path}>Create an account</Link>
                <Link className={`w-1/2 border-algae-200 p-2 border-b-2 text-white ${FOCUS_VISIBLE_OUTLINE_INSET}`} to={routes.publicLogin.path}>Log in</Link>
            </div>
            { message && (
                <InlineAlert type='success' className='mt-6'>{ message }</InlineAlert>
            )}
            { accountOnHold && (
                <InlineAlert type='warning' className='mt-6' data-testid='account-on-hold-message'>
                    Your account is currently on hold, so you have been signed out. Log in below to request reactivation, or contact our Support Team by emailing <a className={`text-link ${TEXT_LINK_FOCUS_RING}`} href='mailto:support@neoni-app.com'>support@neoni-app.com</a>.
                </InlineAlert>
            )}
            { logoutNotConfirmed && (
                <InlineAlert type='warning' className='mt-6' data-testid='logout-not-confirmed-message'>
                    This device's local session has been cleared, but we could not confirm with the server that the previous session was ended. If you were using a shared or public device, please close the browser to be safe.
                </InlineAlert>
            )}
            <form onSubmit={onFormSubmit}>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Email address</span>
                    <br />
                    <input
                        disabled={submitting}
                        id="emailField"
                        name="email"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="text"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                    />
                </label>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Password</span>
                    <br />
                    <input
                        disabled={submitting}
                        id="passwordField"
                        name="password"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                    />
                </label>
                { error && (
                    <InlineAlert type='error' className='mt-6'>{ error }</InlineAlert>
                )}
                { notVerified && (
                    resent
                        ? <InlineAlert type='success' className='mt-6'>If this address is awaiting verification, a new verification email is on its way.</InlineAlert>
                        : (
                            <div className='mt-4'>
                                <Button type='Outlined' text='Resend verification email' disabled={submitting} onClick={onResendVerification} />
                            </div>
                        )
                )}

                <div className='mt-6 flex justify-start'>
                    <Button
                        disabled={submitting}
                        buttonType="submit"
                        type="Primary"
                        text="Login"
                        context="form-field"
                    />
                </div>
                <div className="mt-6 text-charcoal-100">
                    <Link
                        className={`text-link text-charcoal-100 ${TEXT_LINK_FOCUS_RING}`}
                        to={routes.forgotPassword.path}
                    >
                        Forgot password?
                    </Link>
                </div>
            </form>
        </div>
    );
}

export default Login
